import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isMatch, structuralDiff } from '../utils/structuralDiff';

interface StructuralDiffSummaryProps {
  candidate: string;
  template?: string | null;
}

const prettyLeaf = (path: string): { name: string; location: string; isAttribute: boolean } => {
  const segments = path.split('/');
  const last = segments[segments.length - 1];
  const isAttribute = last.startsWith('@');
  return {
    name: isAttribute ? last.slice(1) : last,
    location: segments.slice(0, -1).join(' › '),
    isAttribute,
  };
};

const Group: React.FC<{ title: string; items: string[]; variant: string; icon: string }> = ({
  title,
  items,
  variant,
  icon,
}) => {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <div className="mb-3">
      <div className={`fw-semibold text-${variant} mb-1`}>
        <i className={`bi ${icon} me-1`} />
        {title} ({items.length})
      </div>
      <ul className="list-unstyled small mb-0 ms-4">
        {items.map((p) => {
          const { name, location, isAttribute } = prettyLeaf(p);
          return (
            <li key={p} className="d-flex flex-wrap align-items-baseline gap-2 mb-1">
              <code className="text-body fw-semibold">{name}</code>
              {isAttribute && <span className="badge bg-light text-secondary border">{t('Attribute')}</span>}
              {location && <span className="text-muted">{location}</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const StructuralDiffSummary: React.FC<StructuralDiffSummaryProps> = ({ candidate, template }) => {
  const { t } = useTranslation();
  const result = useMemo(() => (template ? structuralDiff(candidate, template) : null), [candidate, template]);

  if (!result) return null;

  if (!result.comparable) {
    return <div className="alert alert-warning py-2 small mb-3">{t('FormatDiffers')}</div>;
  }

  if (isMatch(result)) {
    return (
      <div className="alert alert-success py-2 small mb-3">
        <i className="bi bi-check-circle" /> {t('StructureMatches')}
      </div>
    );
  }

  return (
    <div className="border rounded p-2 mb-3">
      <div className="fw-semibold mb-2">{t('StructureCheck')}</div>
      <Group title={t('MissingTags')} items={result.missing} variant="danger" icon="bi-dash-circle" />
      <Group title={t('ExtraTags')} items={result.extra} variant="primary" icon="bi-plus-circle" />
      <Group title={t('EmptyValues')} items={result.emptyValues} variant="warning" icon="bi-exclamation-triangle" />
    </div>
  );
};

export default StructuralDiffSummary;
